import { LoginResultDto, UserDto, validInt } from 'booksprout'
import { Platform } from 'quasar'
import state, { resetAuthenticationState } from './state'
import { AuthConsentPayload } from './types'

/**
 * The Vuex auth mutations as `_`-prefixed functions. Behaviour is carried over verbatim; the
 * Vuex mutations now delegate here so there is a single source of truth while the actions still
 * live in Vuex (they make API calls, which move in stage 4).
 */

export function _resetState () {
  Object.assign(state(), resetAuthenticationState())
}

/**
 * Page reload: seed the state from whatever the cookie or localStorage had, then again once the
 * server has confirmed who the user is.
 */
export function _authLoad (data: Pick<LoginResultDto, 'user' | 'token'> & { mimicUser?: UserDto }) {
  const s = state()

  s.user = data.user
  s.token = data.token

  // Only overwrite the mimic user when one was actually supplied — a plain reload carries none,
  // and clobbering it here would drop an active mimic session.
  if (validInt(data.mimicUser?.id)) {
    s.mimicUser = data.mimicUser
  }
}

export function _authSuccess (loginResult: LoginResultDto) {
  const s = state()

  /**
   * iOS Capacitor keeps the token in localStorage, so the token data has to be retained. Every
   * other platform holds it in an httpOnly cookie and deliberately stores only the expiry, so
   * the token itself never reaches client-side state (or the SSR payload).
   */
  if (Platform.is.ios) {
    s.token = loginResult.token
  } else {
    s.token = {
      data: '',
      expires: loginResult.token.expires
    }
  }

  s.user = loginResult.user

  if (validInt(loginResult.mimicUser?.id)) {
    s.mimicUser = loginResult.mimicUser
  } else {
    // An auth with no mimic must clear any mimic left over from a previous session.
    s.mimicUser = void 0
  }
}

export function _authLogout () {
  Object.assign(state(), resetAuthenticationState())
}

export function _setConsentFailed (payload: AuthConsentPayload | null) {
  if (payload === null) {
    Object.assign(state(), resetAuthenticationState())
    return
  }

  const s = state()
  s.termsConsentFailed = payload.loginResult.termsConsentFailed
  // The user is needed while telling them consent is required, but without a login token.
  s.user = payload.loginResult.user
  s.termsConsentData.loginResult = payload.loginResult
}

export function _setConsentNeeded (payload: AuthConsentPayload | null) {
  if (payload === null) {
    Object.assign(state(), resetAuthenticationState())
    return
  }

  const s = state()
  s.termsConsentNeeded = payload.loginResult.termsConsentNeeded
  s.user = payload.loginResult.user
  s.termsConsentData.loginResult = payload.loginResult
}

export function _setAuthExpired (hasExpired: boolean) {
  state().authExpired = hasExpired
}

/**
 * The `!== void 0` guard is carried over from the Vuex mutation: it means a user cannot be set
 * onto state that has been fully reset, which is what stops a logout race from resurrecting one.
 */
export function _setUser (user: UserDto) {
  const s = state()

  if (s.user !== void 0) {
    s.user = user
  }
}

export function _setCsrfToken (token: string | undefined) {
  state().csrfToken = token
}
